import * as zod from 'zod';

/**
 * One journaled write to a routine document, made by something other than the
 * routine editor — the agent's tools today. The editor loads a routine's rows
 * to offer undo for edits it did not see happen.
 *
 * Full-row schema, DB owns defaults (see agentSession.ts for the convention).
 */
export const RoutineEditOrigin = zod.enum(['agent']);
export type RoutineEditOrigin = zod.infer<typeof RoutineEditOrigin>;

/**
 * A field-scoped change in wire form. `path` is a top-level routine field or
 * `stepConfigurations.<stepID>`; a side the document did not have is the
 * marker `{ "$absent": true }` (JSON cannot carry the in-memory symbol). The
 * encode/decode pair lives in `@sb/remote-control/util/routine-edit`.
 */
export const SerializedFieldChange = zod.object({
  path: zod.string(),
  before: zod.unknown(),
  after: zod.unknown(),
});
export type SerializedFieldChange = zod.infer<typeof SerializedFieldChange>;

export const RoutineEditData = zod.object({
  id: zod.string(),
  routineID: zod.string(),
  origin: RoutineEditOrigin,
  /** The agent session that made the edit, when known. */
  actorID: zod.string().nullable(),
  /** Shared by every write of one agent turn; the editor folds them into one undo. */
  turnID: zod.string().nullable(),
  toolName: zod.string().nullable(),
  // A row that changed nothing has nothing to undo, so it is never written.
  changes: zod.array(SerializedFieldChange).min(1),
  /** Set once the editor has undone this row; cleared again on redo. */
  undoneAt: zod.string().datetime().nullable(),
  createdAt: zod.string().datetime(),
  updatedAt: zod.string().datetime(),
});
export type RoutineEditData = zod.infer<typeof RoutineEditData>;

/**
 * The only thing a client may change on a row: whether it has been undone.
 * `updatedAt` stays so the patch chain's `timestamp('updatedAt')` hook is not
 * stripped by `parse()` (same reasoning as `AgentSessionPatchInput`).
 */
export const RoutineEditPatchInput = RoutineEditData.pick({
  undoneAt: true,
  updatedAt: true,
});
export type RoutineEditPatchInput = zod.infer<typeof RoutineEditPatchInput>;
